import type { EmailTemplateInput } from "@/schema/email-template"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  cloneEmailTemplate,
  createEmailTemplate,
  destroyEmailTemplate,
  renderEmailTemplate,
  replacePlaceholders,
  ReplacePlaceholdersParams,
  updateEmailTemplate,
} from "./api"

export function useCreateEmailTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ emailTemplate, scope }: { emailTemplate: EmailTemplateInput; scope?: "company" | "book" }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createEmailTemplate(context, emailTemplate, scope ? { scope } : undefined)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] })
    },
  })
}

export function useUpdateEmailTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, emailTemplate }: { id: string; emailTemplate: Partial<EmailTemplateInput> }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateEmailTemplate(context, id, emailTemplate)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] })
    },
  })
}

export function useDestroyEmailTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return destroyEmailTemplate(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] })
    },
  })
}

export const useDeleteEmailTemplate = useDestroyEmailTemplate

export function useCloneEmailTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, name, bookId }: { id: string; name: string; bookId?: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return cloneEmailTemplate(context, id, name, bookId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] })
    },
  })
}

export function useRenderEmailTemplate() {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async ({ emailTemplateId, householdId }: { emailTemplateId: string; householdId?: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return renderEmailTemplate(context, emailTemplateId, householdId)
    },
  })
}

export function useReplacePlaceholders() {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async (params: ReplacePlaceholdersParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return replacePlaceholders(context, params)
    },
  })
}
